import { useEffect, useRef, useState } from "react";

import { getProfile, putProfile } from "../api/client";
import type { ProfilePreferences } from "../types/api";

/**
 * ProfilePreferencesSection — chat-personalization Task 10.2.
 *
 * Self-contained section for the Account page. Fetches preferences on mount,
 * shows a loading skeleton while in flight, then renders the form. Available to
 * all authenticated tiers (free and Pro). On save, calls PUT /user/profile which
 * invalidates the chat personalization cache server-side.
 */
export function ProfilePreferencesSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const [displayName, setDisplayName] = useState("");
  const [searchRequirements, setSearchRequirements] = useState("");
  const [targetAreas, setTargetAreas] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  const feedbackTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    getProfile()
      .then((prefs: ProfilePreferences) => {
        if (cancelled) return;
        setDisplayName(prefs.display_name || "");
        setSearchRequirements(prefs.search_requirements || "");
        setTargetAreas(prefs.target_areas || []);
      })
      .catch(() => {
        if (cancelled) return;
        // Non-blocking — the section renders empty form fields on failure.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Clear feedback after 4 seconds
  useEffect(() => {
    if (!feedback) return;
    if (feedbackTimeoutRef.current !== null) {
      window.clearTimeout(feedbackTimeoutRef.current);
    }
    feedbackTimeoutRef.current = window.setTimeout(() => {
      setFeedback(null);
    }, 4000);
    return () => {
      if (feedbackTimeoutRef.current !== null) {
        window.clearTimeout(feedbackTimeoutRef.current);
      }
    };
  }, [feedback]);

  function addTag(value: string) {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (trimmed.length > 100) return;
    if (targetAreas.length >= 5) return;
    if (targetAreas.some((t) => t.toLowerCase() === trimmed.toLowerCase())) return;
    setTargetAreas([...targetAreas, trimmed]);
    setTagInput("");
  }

  function removeTag(index: number) {
    setTargetAreas(targetAreas.filter((_, i) => i !== index));
  }

  function handleTagKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(tagInput.replace(/,/g, ""));
    }
  }

  async function handleSave() {
    setSaving(true);
    setFeedback(null);
    try {
      await putProfile({
        display_name: displayName || null,
        search_requirements: searchRequirements || null,
        target_areas: targetAreas.length > 0 ? targetAreas : null,
      });
      setFeedback({ type: "success", message: "Preferences saved." });
    } catch (e) {
      const msg = (e as { message?: string }).message || "Could not save preferences.";
      setFeedback({ type: "error", message: msg });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <section className="account-section profile-prefs-section">
        <h2>Profile preferences</h2>
        <div className="profile-prefs-skeleton">
          <span className="pt-skel pt-skel-line" />
          <span className="pt-skel pt-skel-line" />
          <span className="pt-skel pt-skel-line-short" />
        </div>
      </section>
    );
  }

  return (
    <section className="account-section profile-prefs-section">
      <h2>Profile preferences</h2>
      <p className="profile-prefs-hint">
        Help the AI analyst personalize your chat experience.
      </p>

      <div className="profile-prefs-form">
        <label className="profile-prefs-label">
          Display name
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={50}
            placeholder="How should the AI address you?"
            aria-label="Display name"
          />
        </label>

        <label className="profile-prefs-label">
          What are you looking for?
          <textarea
            value={searchRequirements}
            onChange={(e) => setSearchRequirements(e.target.value)}
            maxLength={500}
            placeholder="Describe what you're looking for — the AI analyst will use this to personalize your chat"
            rows={3}
            aria-label="Search requirements"
          />
          <span className="field-hint">{searchRequirements.length}/500</span>
        </label>

        <div className="profile-prefs-label">
          <span className="profile-prefs-label-text">Target areas</span>
          <div className="tag-input-container">
            {targetAreas.map((area, i) => (
              <span key={i} className="tag-chip">
                {area}
                <button
                  type="button"
                  className="tag-remove"
                  onClick={() => removeTag(i)}
                  aria-label={`Remove ${area}`}
                >
                  &times;
                </button>
              </span>
            ))}
            {targetAreas.length < 5 && (
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                onBlur={() => {
                  if (tagInput.trim()) addTag(tagInput);
                }}
                maxLength={100}
                placeholder={
                  targetAreas.length === 0
                    ? "e.g., Historic District, Ardsley Park"
                    : "Add another area"
                }
                className="tag-input"
                aria-label="Add target area"
              />
            )}
          </div>
          {targetAreas.length >= 5 && (
            <span className="field-hint">Maximum 5 areas reached.</span>
          )}
        </div>

        <div className="profile-prefs-actions">
          <button
            type="button"
            className="submit"
            onClick={() => void handleSave()}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save preferences"}
          </button>
          {feedback && (
            <p
              className={feedback.type === "success" ? "profile-prefs-success" : "error"}
              role={feedback.type === "error" ? "alert" : "status"}
            >
              {feedback.message}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
